import { push, ref, set, update } from "firebase/database";
import { db } from "../firebaseConfig";
import { PartnerInvestmentData } from "../../models/partnerInvestment";
import {
	buildPartnerInvestmentPayload,
	validatePartnerInvestmentDraft
} from "../../utils/partnerInvestmentFlow";

const generateInvestmentCode = () => {
	const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
	const suffix = Math.random().toString(36).slice(2, 7).toUpperCase();
	return `STK-${datePart}-${suffix}`;
};

export const savePartnerInvestment = async (
	investment: PartnerInvestmentData
) => {
	const validationError = validatePartnerInvestmentDraft({
		fundingSource: investment.fundingSource,
		partnerName: investment.partnerName,
		amount: investment.amount,
		investmentDate: investment.investmentDate,
		description: investment.description,
		itemLinks: investment.itemLinks,
		shopCashRef: investment.shopCashRef
	});
	if (validationError) {
		throw new Error(validationError);
	}

	const payload = buildPartnerInvestmentPayload({
		...investment,
		amount: investment.amount,
		investmentDate: investment.investmentDate,
		description: investment.description,
		itemLinks: investment.itemLinks,
		createdAt: investment.createdAt,
		userId: investment.userId,
		username: investment.username,
		code: investment.code
	});
	const code = payload.code?.trim() || generateInvestmentCode();
	const normalizedPayload = {
		...payload,
		code,
		createdAt: payload.createdAt || new Date().toISOString()
	};

	const investmentsRef = ref(db, "partnerInvestments");
	const newInvestmentRef = push(investmentsRef);
	const investmentKey = newInvestmentRef.key as string;

	if (!normalizedPayload.itemLinks.length) {
		await set(newInvestmentRef, normalizedPayload);
		return investmentKey;
	}

	const uniqueItemKeys = new Set(
		normalizedPayload.itemLinks.map((link) => link.itemKey)
	);
	if (uniqueItemKeys.size !== normalizedPayload.itemLinks.length) {
		throw new Error("Duplicate items are not allowed in one investment.");
	}

	const updates: Record<string, any> = {
		[`partnerInvestments/${investmentKey}`]: normalizedPayload
	};

	await update(ref(db), updates);
	return investmentKey;
};
